import {sx} from './sx'
import React, {ChangeEvent} from 'react'
import {useTranslation} from "react-i18next";
import ErrorBlock from "@ui/errors/errorBlock/ErrorBlock";
import {CircularProgress, createFilterOptions, FormControl, FormHelperText, TextField} from "@mui/material";
import {classNames} from "@helpers/classNames";
import {useGetSelectData} from "@ui/select/hooks/useGetSelectData";
import {ISelectProps} from "@ui/select/types";
import AutocompleteWrapperSearch from "@ui/select/selectSearch/AutocompleteWrapperSearch";
import {SxType} from "@baseTypes/sx/sxTypes";


interface IProps<T> extends ISelectProps<T> {
    InputProps?: {
        startAdornment?: React.ReactNode;
        endAdornment?: React.ReactNode;
    },
    sx?: {
        input?: SxType
    }
    popperClassName?: string
}

function SelectSearch<T>({
                             data,
                             value,
                             initialValue,
                             onChange,
                             setPage,

                             isLoading,
                             isError,
                             error,
                             errorMessage,
                             disabled,
                             disabledPointerEvents,

                             placeholder,
                             width,
                             label,
                             size = 'small',
                             keyValue,
                             InputProps,
                             sx: sxProps = {},
                             popperClassName,
                             renderOptionContent,
                             getSearchText,
                         }: IProps<T>) {
    const {t} = useTranslation()
    const {arr, obj} = useGetSelectData<T>({data, keyValue})

    // when a consumer supplies extra searchable text, match on label + that text;
    // otherwise leave MUI's default filtering untouched
    const filterOptions = React.useMemo(() => {
        if (!getSearchText) {
            return undefined
        }
        return createFilterOptions<string>({
            stringify: (option) => {
                const info = obj?.[option]
                return info ? `${info.label} ${getSearchText(info)}` : option
            }
        })
    }, [obj, getSearchText])
    const handleChange = (event: ChangeEvent<HTMLInputElement>, value: string) => {
        if (setPage) {
            setPage(1)
        }
        if (obj.hasOwnProperty(value)) {
            const data = obj[value];
            onChange(value, data?.fullData as T)
            return
        }
        onChange(value)
    }

    if (isError) {
        return <ErrorBlock/>
    }

    return (
        <FormControl
            className={classNames(disabledPointerEvents && 'disabledPointerEvents', (disabled || isLoading) && 'disabledSelect')}
            error={error}
            sx={{
                width: width ? width : '100%',
                position: 'relative'
            }}
            size={size}
        >
            {isLoading && <CircularProgress sx={sx.spinner} size={20}/>}

            <AutocompleteWrapperSearch
                sx={sx.root}
                loadingText={t('base.isLoading')}
                disabled={disabled}
                className={classNames(error && 'error')}
                size={size}
                value={value}
                defaultValue={initialValue}
                noOptionsText={t('notFound')}
                onChange={handleChange}
                loading={isLoading}
                options={arr}
                classes={{
                    popper:popperClassName
                }}
                getOptionLabel={(item: string) => {
                    if (obj?.hasOwnProperty(item)) {
                        const info = obj[item]
                        return info?.label
                    }
                    return item
                }}
                filterOptions={filterOptions}
                renderOption={(props, option: string) => {
                    const info = obj?.hasOwnProperty(option) ? obj[option] : null
                    if (info && renderOptionContent) {
                        return <li {...props}>
                            {renderOptionContent(info)}
                        </li>
                    }
                    return <li {...props}>
                        {info?.label ?? option}
                    </li>
                }}
                renderInput={(props) => (
                    <TextField
                        {...props}
                        error={error}
                        size={size}
                        label={label}
                        placeholder={placeholder}
                        sx={{
                            ...sx.input,
                            ...(sxProps?.input ?? {})
                        }}
                        InputProps={{
                            ...props.InputProps,
                            startAdornment: InputProps?.startAdornment ?? props?.InputProps?.startAdornment,
                            endAdornment: InputProps?.endAdornment ?? props?.InputProps?.endAdornment,
                        }}
                    />
                )}
            />

            {error && errorMessage && <FormHelperText error>
                {errorMessage}
            </FormHelperText>}
        </FormControl>
    )
}


export default SelectSearch